import { P2GCheckBox, P2GGrid, P2GLink, P2GText } from '@parcel2go/core-ui';
import { makeStyles } from 'tss-react/mui';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { ProhibInfo } from './prohib-info';
import { useCallback, useMemo, useState } from 'react';
import { Currency } from '~/lib/types/basket-types';
import { toDisplayCurrency } from '~/lib/prices';
import { ErrorMessage } from '~/components/core/error-message/error-message';
import { ServiceTerms } from './service-terms';
import { useContentKeys, useTaxName } from '@parcel2go/npm-p2g-app';

const useStyles = makeStyles()(() => ({
  check: {
    maxWidth: '100%'
  }
}));

type ProhibitedProps = {
  serviceTermsChecked?: boolean;
  onServiceTermsChange?: (checked: boolean) => void;
  serviceTerms?: { link: string; courier: string };
  checked: boolean;
  hasTax: boolean;
  onChange: (checked: boolean) => void;
  errors: Record<string, string>;
  protectionPrice?: number;
  requiresCustoms: boolean;
  currency?: Currency;
};

export const Prohibited = ({
  errors,
  hasTax,
  checked,
  onChange,
  protectionPrice,
  currency,
  serviceTerms,
  serviceTermsChecked,
  onServiceTermsChange,
  requiresCustoms
}: ProhibitedProps) => {
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const taxName = useTaxName();
  const { classes } = useStyles();
  const [open, setOpen] = useState(false);
  const [inlineValid, setInlineValid] = useState(false);

  const contentKeys = useContentKeys();
  const serviceTermsContentKey = 'service-terms-info';
  const hasServiceTermsContent = contentKeys.includes(serviceTermsContentKey);
  const [serviceTermsInlineValid, setServiceTermsInlineValid] = useState(false);
  const [serviceTermsOpen, setServiceTermsOpen] = useState(false);

  const price = useMemo(() => {
    if (!protectionPrice || !currency) {
      return '';
    }
    return hasTax
      ? t('PRICE_EXC', {
          defaultValue: '{{price}} exc {{taxName}}',
          price: toDisplayCurrency(currency, protectionPrice),
          taxName: taxName
        })
      : toDisplayCurrency(currency, protectionPrice);
  }, [protectionPrice, currency, hasTax, t, taxName]);

  const serviceIsError = !serviceTermsInlineValid && !!errors['chkServiceTerms'];
  const handleServiceTermsCheck = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setServiceTermsInlineValid(!e.currentTarget.checkValidity || e.currentTarget.checkValidity());
      onServiceTermsChange?.(e.currentTarget.checked);
    },
    [onChange]
  );
  const openServiceTerms = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      setServiceTermsOpen(true);
    },
    [setServiceTermsOpen]
  );

  const isError = !inlineValid && !!errors['chkProhibited'];
  const handleCheck = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInlineValid(!e.currentTarget.checkValidity || e.currentTarget.checkValidity());
      onChange(e.currentTarget.checked);
    },
    [onChange]
  );
  const openProhibInfo = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      setOpen(true);
    },
    [setOpen]
  );

  return (
    <P2GGrid container direction="column" gap={1} dataTestId="prohibited-terms">
      {serviceTerms && (
        <P2GGrid item>
          <P2GGrid container direction="row" spacing={2} alignItems="center" pt={1} wrap="nowrap">
            <P2GGrid item>
              <P2GCheckBox
                size="small"
                dataTestId="service-terms-checkbox"
                classNames={classes.check}
                error={serviceIsError}
                labelPlacement="end"
                variant="primary"
                inputProps={{
                  required: true,
                  id: 'chkServiceTerms',
                  onChange: handleServiceTermsCheck,
                  checked: serviceTermsChecked
                }}
                label={null}
              />
            </P2GGrid>
            <P2GGrid item>
              <P2GText dataTestId="service-terms-checkbox-text">
                <Trans
                  i18nKey="PARCELS_SERVICE_TERMS"
                  t={t}
                  values={{ courier: serviceTerms.courier }}
                  defaults="Please confirm you have read, understood and accept the {{courier}} <0>terms and conditions</0>."
                  components={[
                    hasServiceTermsContent ? (
                      <P2GLink onClick={openServiceTerms} />
                    ) : (
                      <P2GLink href={serviceTerms.link} target="_blank" />
                    )
                  ]}
                />
              </P2GText>
            </P2GGrid>
          </P2GGrid>
          {hasServiceTermsContent && (
            <ServiceTerms
              open={serviceTermsOpen}
              onClose={() => setServiceTermsOpen(false)}
              contentKey={serviceTermsContentKey}
            />
          )}
        </P2GGrid>
      )}
      <P2GGrid item>
        <P2GGrid container direction="row" spacing={2} alignItems="center" pt={1} wrap="nowrap">
          <P2GGrid item>
            <P2GCheckBox
              size="small"
              dataTestId="prohibited-terms-checkbox"
              classNames={classes.check}
              error={isError}
              labelPlacement="end"
              variant="primary"
              inputProps={{ required: true, id: 'chkProhibited', onChange: handleCheck, checked: checked }}
              label={null}
            />
          </P2GGrid>
          <P2GGrid item>
            <P2GText dataTestId="prohibited-terms-checkbox-text">
              {requiresCustoms ? (
                price ? (
                  <Trans
                    i18nKey="PARCELS_CUSTOMS_PROHIBITED_TERMS_AND_PROTECTION"
                    t={t}
                    defaults="I agree to pay the additional {{price}} to fully protect my parcel and I confirm that the information provided, including commodity code(s), is accurate to the best of my knowledge, and I have listed different items separately. This parcel also complies with the <0>prohibited & restricted</0> items list."
                    values={{ price: price }}
                    components={[<P2GLink onClick={openProhibInfo} />]}
                  />
                ) : (
                  <Trans
                    i18nKey="PARCELS_CUSTOMS_PROHIBITED_TERMS"
                    t={t}
                    defaults="I confirm that the information provided, including commodity code(s), is accurate to the best of my knowledge, and I have listed different items separately. This parcel also complies with the <0>prohibited & restricted</0> items list."
                    components={[<P2GLink onClick={openProhibInfo} />]}
                  />
                )
              ) : price ? (
                <Trans
                  i18nKey="PARCELS_PROHIBITED_TERMS_AND_PROTECTION"
                  t={t}
                  defaults="I agree to pay the additional {{price}} to fully protect my parcel. This parcel also complies with the <0>prohibited & restricted</0> items list."
                  values={{ price: price }}
                  components={[<P2GLink onClick={openProhibInfo} />]}
                />
              ) : (
                <Trans
                  i18nKey="PARCELS_PROHIBITED_TERMS"
                  t={t}
                  defaults="This parcel complies with the <0>prohibited & restricted</0> items list."
                  components={[<P2GLink onClick={openProhibInfo} />]}
                />
              )}
            </P2GText>
          </P2GGrid>
        </P2GGrid>
        <ProhibInfo open={open} onClose={() => setOpen(false)} />
      </P2GGrid>
      {isError && (
        <P2GGrid item>
          <ErrorMessage message={errors['chkProhibited']} dataTestId="prohibited-terms-checkbox-error" />
        </P2GGrid>
      )}
      {serviceIsError && !isError && (
        <P2GGrid item>
          <ErrorMessage message={errors['chkServiceTerms']} dataTestId="service-terms-checkbox-error" />
        </P2GGrid>
      )}
    </P2GGrid>
  );
};
